'use client'

import { useState } from 'react'
import { useCartStore } from '@/stores/cartStore'
import ShellButton from '@/components/ShellButton'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

interface AddToCartButtonProps {
  productId: string;
  moqGrams: number;
  addToCartText: string;
  quantityLabel: string;
  defaultUnit?: string;
  unitOptions?: Array<{value: string, label: string}>;
  defaultQuantity?: number;
  conversionFactor?: number;
  productOptions?: Array<{name: string, extraPrice: number}>;
  kg_price?: boolean;
}

export default function AddToCartButton({ 
  productId, 
  moqGrams, 
  addToCartText, 
  quantityLabel,
  defaultUnit = "kg",
  unitOptions = [
    { value: "kg", label: "kg" },
    { value: "g", label: "g" }
  ],
  defaultQuantity = 1,
  conversionFactor = 1000,
  productOptions,
  kg_price = true
}: AddToCartButtonProps) {
  const t = useTranslations('productDetail')
  const [quantity, setQuantity] = useState(defaultQuantity)
  const [unit, setUnit] = useState(defaultUnit)
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    productOptions && productOptions.length > 0 ? productOptions[0].name : undefined
  )
  const [isAdding, setIsAdding] = useState(false)
  const [success, setSuccess] = useState(false)
  const router = useRouter()
  
  // Get addItem function from cart store
  const addItem = useCartStore(state => state.addItem)

  // Calculate the actual grams based on the selected unit
  const getGramsValue = () => {
    // For unit-based products, just return the quantity
    if (!kg_price) {
      return quantity;
    }
    // For kg-based products, convert to grams
    return unit === "kg" ? quantity * conversionFactor : quantity;
  }

  // Ensure the quantity meets the MOQ
  const isValidQuantity = () => {
    // For unit-based products, the minimum is 1 unit
    if (!kg_price) {
      return quantity >= 1;
    }
    // For kg-based products, check against MOQ
    return getGramsValue() >= moqGrams;
  }

  // Get the extra price for the selected option
  const getExtraPrice = () => {
    if (!selectedOption || !productOptions) return 0;
    const option = productOptions.find(opt => opt.name === selectedOption);
    return option ? option.extraPrice : 0;
  }

  // Update the handleAddToCart function to ensure the selected option is passed to the cart
  const handleAddToCart = async () => {
    if (!isValidQuantity()) {
      if (kg_price) {
        alert(`Minimum order quantity is ${moqGrams}g`);
      } else {
        alert(`Minimum order quantity is 1 unit`);
      }
      return;
    }

    setIsAdding(true);
    try {
      // Add to cart using Zustand store with the selected option
      addItem(productId, getGramsValue(), selectedOption);
      
      // Show success state
      setSuccess(true);
      
      // Reset success state after 2 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
      
      // Refresh the page to update any cart indicators
      router.refresh();
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center">
        <label htmlFor="quantity-input" className="text-sm font-bold text-text-main mr-3">
          {quantityLabel}:
        </label>
        <input
          id="quantity-input"
          type="number"
          min={kg_price ? (unit === "kg" ? moqGrams / conversionFactor : moqGrams) : 1}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
          className="w-20 px-3 py-2 bg-surface text-text-main focus:outline-none focus:ring-1 focus:ring-primary rounded-l-lg"
          aria-label={`${quantityLabel} ${kg_price ? `in ${unit}` : 'in units'}`}
        />
        {kg_price && (
          <div className="relative">
            <label htmlFor="unit-select" className="sr-only">Unit</label>
            <select
              id="unit-select"
              value={unit}
              onChange={(e) => {
                const newUnit = e.target.value;
                setUnit(newUnit);
                
                // Adjust quantity when switching units to maintain equivalent value
                if (newUnit !== unit) {
                  if (newUnit === "g" && unit === "kg") {
                    // Convert kg to g (multiply by 1000)
                    setQuantity(quantity * conversionFactor);
                  } else if (newUnit === "kg" && unit === "g") {
                    // Convert g to kg (divide by 1000)
                    setQuantity(Math.max(1, Math.floor(quantity / conversionFactor)));
                  }
                }
              }}
              className="h-full px-3 py-2 rounded-r-lg bg-surface-hover text-text-main appearance-none pr-8 focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="Select unit"
            >
              {unitOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        )}
      </div>
      
      {/* Product Options Selector */}
      {productOptions && productOptions.length > 0 && (
        <div className="mb-4">
          <label htmlFor="option-select" className="block text-sm font-bold text-text-secondary mb-1">
            Color/Option:
          </label>
          <div className="relative">
            <select
              id="option-select"
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-bg-secondary text-text-main appearance-none pr-8 focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="Select product option"
            >
              {productOptions.map(option => (
                <option key={option.name} value={option.name}>
                  {option.name}{option.extraPrice > 0 ? ` (+€${option.extraPrice})` : ''}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </div>
      )}
      
      {!isValidQuantity() && (
        <div className="text-status-error text-sm">
          {kg_price 
            ? t('cart.minOrder', { moq: moqGrams }) 
            : t('cart.minOrderUnits', { moq: 1, defaultValue: 'Minimum order quantity is 1 unit' })}
        </div>
      )}

      {getExtraPrice() > 0 && (
        <div className="text-sm text-text-secondary relative -top-2">
          <span>{t('cart.extraPrice', { option: selectedOption, price: getExtraPrice() })}</span>
        </div>
      )}

      <div className="pt-4 w-full" onClick={(e) => e.stopPropagation()}>
        <div className="pointer-events-none">
          <ShellButton
            type="button"
            variant="white"
            onClick={handleAddToCart}
            disabled={isAdding || !isValidQuantity()}
            className="w-full pointer-events-auto"
          >
            {isAdding ? t('cart.adding') : success ? t('cart.added') : addToCartText}
          </ShellButton>
        </div>
      </div>
    </div>
  )
} 